import React, { useState, useEffect } from "react";
import { X, Upload, Trash2, Search, UserIcon, Phone, Mail, ChevronDown, Loader2 } from "lucide-react";
import { apiService } from "../services/api";
import { BASE_URL } from "../config";
import axios from "axios";
import { getStoredAmenities, getStoredDistricts, getStoredOwners, getStoredRegions } from "../utils/auth";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";


interface Owner {
  id: number;
  //ownerId?: number;
  //ownerName: string;
  name: string;
  //propertyName?: string;
  phone?: string;
  //email?: string;
}

interface FormData {
  ownerId: any;
  regionId: string;
  districtId: string;
  accommodationId: string;
  propertyName: string;
  address: string;
  contactName: string;
  phone: string;
  description: string;
  basePrice: string;
  rating: string;
  mapUrl: string;
  totalRooms: number;
  amenities: string[];
  roomTypes: string[];
  roomPricing: {
    single: string;
    double: string;
    triple: string;
    deluxe: string;
  };
  images: Array<File | null>;
}

interface DropdownOption {
  label: string;
  value: string;
}

interface Errors {
  ownerId?: string;
  propertyName?: string;
  address?: string;
  contactName?: string;
  phone?: string;
  regionId?: string;
  districtId?: string;
  accommodationId?: string;
  basePrice?: string;
  rating?: string;
  roomTypes?: string;
  amenities?: string;
  totalRooms?: string;
  mapUrl?: string;
  images?: string;
}

interface AddHotelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  ownerId?: any;
  owner?: any;
}

const ACCOMMODATION_TYPES = [
  { value: "1", label: "Hotel" },
  { value: "2", label: "Lodge" },
  { value: "3", label: "Guesthouse" },
  { value: "4", label: "Airbnb" },
];

const ROOM_TYPES = [
  { value: "single", label: "Single Sharing" },
  { value: "double", label: "Double Sharing" },
  { value: "triple", label: "Triple Sharing" },
  { value: "deluxe", label: "Deluxe" },
];

const RATINGS = ["1", "2", "3", "4", "5"];

const AddHotelModal = ({
  isOpen,
  onClose,
  onSubmit,
  ownerId,
  owner,
}: AddHotelModalProps) => {

  // ── Owner dropdown state ──────────────────────────────────────────────
  const [owners, setOwners] = useState<Owner[]>([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownerSearch, setOwnerSearch] = useState("");
  const [isOwnerDropdownOpen, setIsOwnerDropdownOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(
    owner ? { id: owner.id, name: owner.name || owner.ownerName, phone: owner.phone } : null
  );

  // ── Form state ────────────────────────────────────────────────────────
  const getInitialState = (ownerIdVal: any): FormData => ({
    ownerId: ownerIdVal,
    regionId: "",
    districtId: "",
    accommodationId: "",
    propertyName: "",
    address: "",
    contactName: "",
    phone: "",
    description: "",
    basePrice: "",
    rating: "",
    mapUrl: "",
    totalRooms: 0,
    amenities: [],
    roomTypes: [],
    roomPricing: { single: "", double: "", triple: "", deluxe: "" },
    images: Array(6).fill(null),
  });

  const [formData, setFormData] = useState<FormData>(getInitialState(owner?.id));
  const [errors, setErrors] = useState<Errors>({});
  const [roomPricingErrors, setRoomPricingErrors] = useState<Record<string, string>>({});
  const [regions, setRegions] = useState<DropdownOption[]>([]);
  const [districts, setDistricts] = useState<DropdownOption[]>([]);
  const [amenities, setAmenities] = useState<DropdownOption[]>([]);
  const [imagePreviews, setImagePreviews] = useState<(string | null)[]>(Array(6).fill(null));
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // ── Fetch owners for dropdown ─────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    //fetchOwners();
    const regions = getStoredRegions();
    let dropdownData = regions.map((region: any) => ({
      label: region.name,
      value: region.id,
    }));
    setRegions(dropdownData);
    const owners = getStoredOwners();
    dropdownData = owners.map((owner: any) => ({
      name: owner.name,
      id: owner.id,
      phone: owner.phone,
    }));
    setOwners(dropdownData);
    const amenities = getStoredAmenities();
  }, [isOpen]);



  const getDistrictsByRegion = (selectedRegionId: number) => {
    const districts = getStoredDistricts();
    return districts
      .filter((d: any) => Number(d.regionId) === selectedRegionId)
      .map((d: any) => ({
        label: d.name,
        value: d.id,
      }));
  };


  useEffect(() => {
    if (!isOpen) return;
    const loadData = async () => {
      try {
        //const regionData = await apiService.getRegions();
        const amenityData = await apiService.getAmenities();
        //setRegions(regionData);
        setAmenities(amenityData);
      } catch (err) {
        console.error(err);
      }
    };
    loadData();
  }, [isOpen]);

  // ── Sync selectedOwner → formData.ownerId ────────────────────────────
  useEffect(() => {
    if (selectedOwner) {
      setFormData((prev) => ({ ...prev, ownerId: selectedOwner.id }));
      setErrors((prev) => ({ ...prev, ownerId: undefined }));
    }
  }, [selectedOwner]);

  const filteredOwners = owners.filter((o) => {
    const term = ownerSearch.toLowerCase();
    return (
      o.name?.toLowerCase().includes(term) ||
      o.phone?.includes(term)
      //o.email?.toLowerCase().includes(term)
    );
  });

  const handleOwnerSelect = (o: Owner) => {
    setSelectedOwner(o);
    setIsOwnerDropdownOpen(false);
    setOwnerSearch("");
  };

  // ── Form helpers ──────────────────────────────────────────────────────
  const updateField = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRegionChange = async (regionId: string) => {
    updateField("regionId", regionId);
    updateField("districtId", "");
    //if (!regionId) { setDistricts([]); return; }
    //const data = await apiService.getDistricts(regionId);
    //setDistricts(data);
    const districts = getDistrictsByRegion(Number(regionId));
    setDistricts(districts);
  };

  const handleImageChange = (index: number, file: File | null) => {
    const images = [...formData.images];
    images[index] = file;
    setFormData((prev) => ({ ...prev, images }));
    const previews = [...imagePreviews];
    previews[index] = file ? URL.createObjectURL(file) : null;
    setImagePreviews(previews);
    const hasImage = images.some((img) => img !== null);
    setErrors((prev) => ({ ...prev, images: hasImage ? undefined : "Please upload at least one image" }));
  };

  const toggleAmenity = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(value)
        ? prev.amenities.filter((a) => a !== value)
        : [...prev.amenities, value],
    }));
  };

  const toggleRoomType = (type: string) => {
    setFormData((prev) => ({
      ...prev,
      roomTypes: prev.roomTypes.includes(type)
        ? prev.roomTypes.filter((t) => t !== type)
        : [...prev.roomTypes, type],
    }));
    if (roomPricingErrors[type]) {
      setRoomPricingErrors((prev) => { const u = { ...prev }; delete u[type]; return u; });
    }
  };

  const handleRoomPriceChange = (type: string, value: string) => {
    setFormData((prev) => ({ ...prev, roomPricing: { ...prev.roomPricing, [type]: value } }));
    if (roomPricingErrors[type]) {
      setRoomPricingErrors((prev) => { const u = { ...prev }; delete u[type]; return u; });
    }
  };

  const validateForm = () => {
    const err: Errors = {};

    if (!formData.ownerId) err.ownerId = "Please select an owner";
    if (!formData.propertyName) err.propertyName = "Property name required";
    if (!formData.address) err.address = "Address required";
    if (!formData.contactName) err.contactName = "Contact name required";
    if (!formData.phone) err.phone = "Contact number required";
    if (!formData.regionId) err.regionId = "Region required";
    if (!formData.districtId) err.districtId = "District required";
    if (!formData.accommodationId) err.accommodationId = "Accommodation type required";
    if (!formData.totalRooms) err.totalRooms = "Total Rooms required";
    if (!formData.basePrice) err.basePrice = "Base price required";
    if (!formData.rating) err.rating = "Rating required";
    if (!formData.roomTypes.length) err.roomTypes = "Select at least one room type";
    if (!formData.amenities.length) err.amenities = "Select at least one Amenity";

    const pricingErr: Record<string, string> = {};
    formData.roomTypes.forEach((type) => {
      const price = formData.roomPricing[type as keyof typeof formData.roomPricing];
      if (!price || Number(price) <= 0) pricingErr[type] = "Price required";
    });
    setRoomPricingErrors(pricingErr);
    if (Object.keys(pricingErr).length > 0) {
      err.roomTypes = err.roomTypes || "Please fill in selected room prices";
    }

    if (!formData.images.some((img) => img !== null)) {
      err.images = "Please upload at least one image";
    }

    if (formData.mapUrl && formData.mapUrl !== "https://") {
      const isValid = /^https:\/\/(www\.)?(maps\.google\.com|goo\.gl\/maps|maps\.app\.goo\.gl|google\.com\/maps)/.test(formData.mapUrl);
      if (!isValid) err.mapUrl = "Enter a valid Google Maps URL";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
    const isValid = validateForm();
    if (!isValid) {
      const firstError = document.querySelector(".text-red-500");
      if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (["images", "roomTypes", "roomPricing", "amenities"].includes(key)) return;
        payload.append(key, String(value));
      });

      formData.amenities.forEach((amenity) => payload.append("amenities", amenity));

      const pricingPayload = formData.roomTypes.map((type) => ({
        roomType: type,
        pricePerNight: Number(formData.roomPricing[type as keyof typeof formData.roomPricing] || 0),
      }));
      payload.append("roomPricing", JSON.stringify(pricingPayload));
      formData.images.forEach((img) => { if (img) payload.append("propertyPhotos", img); });

      await axios.post(`${BASE_URL}/properties`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setFormData(getInitialState(null));
        setImagePreviews(Array(6).fill(null));
        setRoomPricingErrors({});
        setSelectedOwner(null);
        onClose();
      }, 4000);
    } catch (err: any) {
      console.error("API Error:", err.response?.data || err.message);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-xl">

        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">Add Property</h2>
          <button type="button" onClick={onClose}>
            <X />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">

          {/* ── OWNER SELECTION DROPDOWN ── */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1.5 block">
              Select Owner *
            </label>

            {/* Trigger */}
            <div
              onClick={() => setIsOwnerDropdownOpen((prev) => !prev)}
              className={`flex items-center justify-between w-full border rounded-lg px-3 py-2.5 cursor-pointer hover:border-brand-400 transition bg-white ${errors.ownerId ? "border-red-400" : "border-gray-300"
                }`}
            >
              {selectedOwner ? (
                <div className="flex items-center gap-2">
                  <UserIcon size={16} className="text-brand-500" />
                  <span className="text-sm font-medium text-gray-800">
                    {selectedOwner.name}
                  </span>
                  {selectedOwner.phone && (
                    <span className="text-xs text-gray-400">· {selectedOwner.phone}</span>
                  )}
                </div>
              ) : (
                <span className="text-sm text-gray-400">Search and select an owner...</span>
              )}
              <ChevronDown
                size={16}
                className={`text-gray-400 transition-transform flex-shrink-0 ${isOwnerDropdownOpen ? "rotate-180" : ""}`}
              />
            </div>

            {errors.ownerId && (
              <p className="text-red-500 text-xs mt-1">{errors.ownerId}</p>
            )}

            {/* Dropdown panel */}
            {isOwnerDropdownOpen && (
              <div className="relative">
                <div className="absolute top-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-10 overflow-hidden">

                  {/* Search inside dropdown */}
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by name or phone..."
                        value={ownerSearch}
                        onChange={(e) => setOwnerSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                      />
                    </div>
                  </div>

                  {/* Owner list */}
                  <div className="max-h-52 overflow-y-auto">
                    {ownersLoading ? (
                      <div className="flex items-center justify-center py-6 gap-2 text-gray-400">
                        <Loader2 size={16} className="animate-spin" />
                        <span className="text-sm">Loading owners...</span>
                      </div>
                    ) : filteredOwners.length === 0 ? (
                      <div className="py-6 text-center text-sm text-gray-400">
                        No owners found
                      </div>
                    ) : (
                      filteredOwners.map((o) => (
                        <div
                          key={o.id}
                          onClick={() => handleOwnerSelect(o)}
                          className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-brand-50 transition ${selectedOwner?.id === o.id ? "bg-brand-50 border-l-2 border-brand-500" : ""
                            }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <UserIcon size={14} className="text-brand-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">
                              {o.name}
                            </p>
                            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                              {o.phone && (
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                  <Phone size={10} /> {o.phone}
                                </span>
                              )}
                              {/* {o.email && (
                                <span className="flex items-center gap-1 text-xs text-gray-400 truncate max-w-[160px]">
                                  <Mail size={10} /> {o.email}
                                </span>
                              )} */}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Selected owner preview card */}

            {/* <div className="mt-3 bg-green-50 border border-green-200 rounded-xl p-3 flex items-start gap-3"> */}

            {/* <div> */}
            {/* <p className="text-sm font-bold text-gray-800">{selectedOwner.name}</p> */}
            {/* {selectedOwner.phone && (
                    // <p className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                    //   <Phone size={11} /> {selectedOwner.phone}
                    // </p>
                  )} */}
            {/* {selectedOwner.email && (
                    <p className="flex items-center gap-1.5 text-xs text-gray-400 mt-0.5">
                      <Mail size={11} /> {selectedOwner.email}
                    </p>
                  )} */}
            {/* </div> */}
            {/* </div> */}

          </div>

          {/* ── REST OF THE FORM (unchanged) ── */}

          {/* Property Name */}
          <div>
            <label className="text-sm font-medium">Property Name *</label>
            <input
              type="text"
              value={formData.propertyName}
              onChange={(e) => updateField("propertyName", e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />
            {errors.propertyName && (
              <p className="text-red-500 text-xs mt-1">{errors.propertyName}</p>
            )}
          </div>

          {/* Address */}
          <div>
            <label className="text-sm font-medium">Address *</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => updateField("address", e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mt-1"
            />
            {errors.address && (
              <p className="text-red-500 text-xs mt-1">{errors.address}</p>
            )}
          </div>
          {/* property contact information  */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">
                Contact name *
              </label>
              <input type="text" value={formData.contactName} onChange={(e) => updateField("contactName", e.target.value)}
                placeholder="Enter contact person name"
                className="w-full border rounded-lg px-3 py-2 mt-1" />
              {errors.contactName && (
                <p className="text-red-500 text-xs mt-1">{errors.contactName}</p>)}
            </div>
            <div>
  <label className="text-sm font-medium">Phone *</label>

  <PhoneInput
    country={"tz"} // Tanzania default
    value={formData.phone}
    onChange={(phone) => updateField("phone", phone)}
    enableSearch={true}
    countryCodeEditable={true}
    inputStyle={{
      width: "100%",
      height: "42px",
    }}
    containerStyle={{
      width: "100%",
      marginTop: "4px",
    }}
  />

  {errors.phone && (
    <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
  )}
</div>

          </div>



          {/* Map URL */}
          <div>
            <label className="text-sm font-medium">Google Map URL</label>
            <div className={`flex items-center border rounded-lg mt-1 overflow-hidden ${errors.mapUrl ? "border-red-400" : "border-gray-300"}`}>
              <input
                type="text"
                value={formData.mapUrl.replace(/^https?:\/\//, "")}
                onChange={(e) => {
                  updateField("mapUrl", "https://" + e.target.value.replace(/^https?:\/\//, ""));
                  if (errors.mapUrl) setErrors((prev) => ({ ...prev, mapUrl: undefined }));
                }}
                placeholder="maps.google.com/..."
                className="flex-1 px-3 py-2 outline-none text-sm"
              />
            </div>
            {errors.mapUrl && (
              <p className="text-red-500 text-xs mt-1">{errors.mapUrl}</p>
            )}
          </div>

          {/* Region / District */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Region *</label>
              <select
                value={formData.regionId}
                onChange={(e) => handleRegionChange(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 mt-1"
              >
                <option value="">Select</option>
                {regions.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              {errors.regionId && (
                <p className="text-red-500 text-xs mt-1">{errors.regionId}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">District *</label>
              <select
                value={formData.districtId}
                onChange={(e) => updateField("districtId", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 mt-1"
              >
                <option value="">Select</option>
                {districts.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
              {errors.districtId && (
                <p className="text-red-500 text-xs mt-1">{errors.districtId}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Accommodation Type *</label>
              <select
                value={formData.accommodationId}
                onChange={(e) => updateField("accommodationId", e.target.value)}
                className="w-full border rounded-lg px-3 py-2 mt-1"
              >
                <option value="">Select Accommodation</option>
                {ACCOMMODATION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {errors.accommodationId && (
                <p className="text-red-500 text-xs mt-1">{errors.accommodationId}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Total Rooms *</label>
              <input
                type="number"
                value={formData.totalRooms}
                onChange={(e) => updateField("totalRooms", e.target.value)}
                placeholder="Enter total rooms"
                className="w-full mt-1 border rounded-lg px-3 py-2 outline-none"
              />
              {errors.totalRooms && (
                <p className="text-red-500 text-xs mt-1">{errors.totalRooms}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Base Price (per night) *</label>
              <input
                type="number"
                value={formData.basePrice}
                onChange={(e) => updateField("basePrice", e.target.value)}
                placeholder="Enter base price"
                className="w-full mt-1 border rounded-lg px-3 py-2 outline-none"
              />
              {errors.basePrice && (
                <p className="text-red-500 text-xs mt-1">{errors.basePrice}</p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Rating *</label>
              <select
                value={formData.rating}
                onChange={(e) => updateField("rating", e.target.value)}
                className="w-full mt-1 border rounded-lg px-3 py-2 outline-none"
              >
                <option value="">Select Rating</option>
                {RATINGS.map((r) => (
                  <option key={r} value={r}>{r} Star</option>
                ))}
              </select>
              {errors.rating && (
                <p className="text-red-500 text-xs mt-1">{errors.rating}</p>
              )}
            </div>
          </div>

          {/* Room Types */}
          <div>
            <label className="text-sm font-medium">Room Types *</label>
            <div className="flex gap-3 mt-3 overflow-x-auto">
              {ROOM_TYPES.map((room) => {
                const active = formData.roomTypes.includes(room.value);
                return (
                  <button
                    key={room.value}
                    type="button"
                    onClick={() => toggleRoomType(room.value)}
                    className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition ${active ? "bg-green-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    {room.label}
                  </button>
                );
              })}
            </div>
            {errors.roomTypes && (
              <p className="text-red-500 text-xs mt-1">{errors.roomTypes}</p>
            )}
            {formData.roomTypes.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-5">
                {formData.roomTypes.map((type) => (
                  <div key={type}>
                    <label className="text-sm capitalize">{type} Price *</label>
                    <input
                      type="number"
                      value={formData.roomPricing[type as keyof typeof formData.roomPricing]}
                      onChange={(e) => handleRoomPriceChange(type, e.target.value)}
                      className={`w-full border rounded-lg px-3 py-2 mt-1 ${roomPricingErrors[type] ? "border-red-400" : "border-gray-300"
                        }`}
                      placeholder="Enter price"
                    />
                    {roomPricingErrors[type] && (
                      <p className="text-red-500 text-xs mt-1">{roomPricingErrors[type]}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Amenities */}
          <div>
            <label className="text-sm font-medium">Amenities *</label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {amenities.map((a) => (
                <label key={a.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.amenities.includes(a.value)}
                    onChange={() => toggleAmenity(a.value)}
                  />
                  {a.label}
                </label>
              ))}
            </div>
            {errors.amenities && (
              <p className="text-red-500 text-xs mt-1">{errors.amenities}</p>
            )}
          </div>

          {/* Images */}
          <div>
            <label className="text-sm font-medium">Images *</label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              {imagePreviews.map((preview, i) => (
                <div key={i} className="relative border rounded-lg p-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(i, e.target.files?.[0] || null)}
                    className="w-full text-sm truncate overflow-hidden whitespace-nowrap"
                  />
                  {preview && (
                    <>
                      <img src={preview} className="mt-2 h-20 w-full object-cover" alt="" />
                      <button type="button" onClick={() => handleImageChange(i, null)} className="absolute top-1 right-1">
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
            {errors.images && (
              <p className="text-red-500 text-xs mt-1">{errors.images}</p>
            )}
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="flex-1 border rounded-lg py-2">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-600 text-white rounded-lg py-2 disabled:opacity-60"
            >
              {loading ? "Adding..." : "Add Property"}
            </button>
          </div>
        </form>
      </div>

      {showSuccess && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl shadow-xl p-8 text-center w-[320px]">
            <div className="flex justify-center mb-4">
              <div className="bg-green-100 p-3 rounded-full">
                <Upload className="text-green-600" size={28} />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-sage-900">Property Uploaded</h3>
            <p className="text-sm text-sage-500 mt-2">
              Property has been successfully submitted. It will be reviewed by admin.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddHotelModal;